import { withModal, WithModal } from "Components/General/Modal/withModal";
import { I18N_NS } from "Config/i18n";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { userSettingsStore } from "Stores/UserSettingsStore";
import { MAIL_LINK } from "../Communication";
import { EmailTypeStore, MailSortBy } from "../Stores/EmailTypeStore";
import { MessageType } from "antd/es/message/interface";
import { message } from "antd";
import { emailUnreadStore } from "../Stores/EmailUnreadStore";
import { projectsDataStore } from "Stores";
import { Routes, useNavigate } from "react-router";
import { observer } from "mobx-react";
import { modal } from "Components/General/Modal/AntdModal";
import { MoveToProjectModal } from "./MoveToProjectModal";
import { DropdownActionConfig, PrimaryActionsConfig } from "Components/General/DetailListActions";
import { emailSearchStore } from "../Stores/EmailSearchStore";
import { RouterStore } from "Config/CustomBrowserRouter";
import { Route } from "react-router";
import { EmailSearchResultsPage } from "./EmailSearchResultsPage";
import { DefaultLayout } from "../DefaultLayout/DefaultLayout";
import { OutlookLayout } from "../OutlookLayout/OutlookLayout";

interface EmailListPageProps extends WithModal {
  emailType: EmailType;
  accessLevel?: "private" | "project";
  isProjectInbox?: boolean;
  emailInboxId?: string | number;
}

export const EmailListPage = withModal(
  observer((props: EmailListPageProps) => {
    const { t } = useTranslation(I18N_NS.PROJECT);
    const navigate = useNavigate();
    const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
    const [selectedEmails, setSelectedEmails] = useState<Array<API_MailResponse>>([]);
    const [layoutMode, setLayoutMode] = useState<API_USERDATA_LAYOUTTYPE>(userSettingsStore.data.layoutType || "SPLIT");

    // Derived Values (was in constructor)
    const emailTypeForSearch = useMemo<MAIL_LINK>(() => {
      const enumKey = props.accessLevel === "project" ? `${props.emailType}_PROJECT` : props.emailType;
      return MAIL_LINK[enumKey] as MAIL_LINK;
    }, [props.accessLevel, props.emailType]);

    // Create EmailStore once per component instance.
    const emailListStoreRef = useRef<EmailTypeStore>();
    if (!emailListStoreRef.current) {
      emailListStoreRef.current = new EmailTypeStore({
        type: props.emailType as API_MAILRESPONSE_TYPE,
        projectSpecific: props.accessLevel === "project",
        isProjectInbox: props.isProjectInbox,
        emailInboxId: props.emailInboxId,
      });
    }
    const emailListStore = emailListStoreRef.current;

    const instantiateEmailStore = () => {
      emailListStoreRef.current = new EmailTypeStore({
        type: props.emailType as API_MAILRESPONSE_TYPE,
        projectSpecific: props.accessLevel === "project",
        isProjectInbox: !!props.isProjectInbox,
        emailInboxId: props.emailInboxId,
      });
    };

    useEffect(() => {
      refreshList();
      return () => {
        emailListStore.clear();
      };
    }, []);

    // componentDidUpdate
    // Keep layoutMode in sync with userSettingsStore
    useEffect(() => {
      if (userSettingsStore.data.layoutType !== layoutMode) {
        setLayoutMode(userSettingsStore.data.layoutType);
      }
    }, [userSettingsStore.data.layoutType]);

    // Recreate EmailStore & refresh list when key props change
    const prevPropsRef = useRef<{
      emailType: string;
      accessLevel: string;
      emailInboxId?: string | number;
    }>({
      emailType: props.emailType,
      accessLevel: props.accessLevel,
      emailInboxId: props.emailInboxId,
    });

    useEffect(() => {
      const prev = prevPropsRef.current;

      // emailType or accessLevel changed
      if (prev.emailType !== props.emailType || prev.accessLevel !== props.accessLevel) {
        instantiateEmailStore();
        refreshList();

        // isProjectInbox & emailInboxId changed
      } else if (props.isProjectInbox && props.emailInboxId && prev.emailInboxId !== props.emailInboxId) {
        navigate(`/project/${projectsDataStore.projectId}/email/topic-inbox`);
        emailListStoreRef.current?.reset?.();
        instantiateEmailStore();
        refreshList();
      }

      // Always update prev ref
      prevPropsRef.current = {
        emailType: props.emailType,
        accessLevel: props.accessLevel,
        emailInboxId: props.emailInboxId,
      };
    }, [props.emailType, props.accessLevel, props.emailInboxId, props.isProjectInbox]);

    const refreshList = async () => {
      let loading: MessageType;
      if (emailListStore.data.length) {
        loading = message.loading(t("Loading..."), 0);
      }
      try {
        const emailsRefresh = emailListStore.refresh;
        const unreadRefresh = emailUnreadStore.fetch;
        await Promise.all([emailsRefresh(), unreadRefresh()]);
      } catch (error) {
        throw error;
      } finally {
        if (loading) {
          loading();
        }
      }
    };
    const deselectEmails = () => {
      setSelectedIds([]);
    };

    const handleSelectRow = (selectedItems: string[], selectedEmails: API_MailResponse[]) => {
      setSelectedIds(selectedItems);
      setSelectedEmails(selectedEmails);
    };

    const handleRequest = async (cb: (id: string[]) => void, id?: string) => {
      if (emailListStore.state === "LOADING") {
        // prevent from multiple fetch requests
        return;
      }
      const emailIds = typeof id === "string" ? [id] : selectedIds;
      if (typeof id !== "string" && !selectedIds.length) {
        // stop if no items are checked
        message.error(t("Please select emails"));
      } else {
        let loader: MessageType;
        // timeout to show loader in case of long async tasks
        const timer = window.setTimeout(() => (loader = message.loading(t("Loading..."), 0)), 750);
        try {
          await cb.call(this, emailIds);
        } catch (err) {
          throw err;
        } finally {
          window.clearTimeout(timer);
          if (loader) {
            loader();
          }
        }
      }
    };

    const handleDelete = (id?: string) => {
      handleRequest((emailIds) => {
        modal.confirm({
          title: t("Delete selected emails?"),
          onOk: async () => {
            await emailListStore.deleteEmails(emailIds, emailListStore.emailType);
            deselectEmails();
            handleNavigateToList();
          },
        });
      }, id);
    };

    const handleDownloadPdf = (id?: string) => {
      handleRequest((emailIds) => emailListStore.downloadPdf(emailIds), id);
    };

    const handleArchive = (id?: string) => {
      handleRequest(async (emailIds) => {
        await emailListStore.moveToArchived(emailIds);
        deselectEmails();
        handleNavigateToList();
      }, id);
    };

    const handleUnArchive = (id?: string) => {
      handleRequest(async (emailIds) => {
        await emailListStore.restoreFromArchived(emailIds);
        deselectEmails();
        handleNavigateToList();
      }, id);
    };

    /**
     * Because this action is used in Email details but propagated from list - we need to have a possibility
     * to pass emailId from currently viewed email to run this action.
     */
    const handleMoveToProject = async (emailId?: string | number) => {
      let selectedItems: API_MailResponse[];
      if (emailId) {
        const found = await emailListStore.getEmailById(emailId.toString());
        selectedItems = [found];
      } else {
        selectedItems = selectedEmails.slice();
      }
      props.showModal(MoveToProjectModal, {
        modalProps: {
          footer: null,
          title: t("Move email to Documents"),
          destroyOnHidden: true,
          style: { minWidth: `70%` },
        },
        componentProps: {
          emailStore: emailListStore,
          goBack: handleGoBack,
          selectedItems: selectedItems,
        },
      });
    };

    const handleSort = async (sortBy: MailSortBy, sortOrder: API_MAILPAGINATION_SORTORDER) => {
      await emailListStore.fetchSortedBy({ sortOrder, sortBy });
    };

    const handleChangeLayout = async (layoutMode: API_USERDATA_LAYOUTTYPE) => {
      try {
        // always return to emails list instead of single email
        if (layoutMode === "LIST") {
          handleNavigateToList();
        }
        setLayoutMode(layoutMode);
        await userSettingsStore.setLayoutType(layoutMode);
      } catch (error) {
        throw error;
      }
    };

    const isIntermediate = useMemo(() => {
      return selectedIds.length > 0 && selectedIds.length < emailListStore.total;
    }, [selectedIds.length, emailListStore.total]);

    const isSelectedAll = useMemo(() => {
      return selectedIds.length && selectedIds.length === emailListStore.total;
    }, [selectedIds.length, emailListStore.total]);

    const linkedMailToolTipText = () => {
      return (
        <span>
          {t("A")}
          <strong> PDF</strong>
          {t(" version of this email will be stored in the folder view of documents and plans. ")}
          <strong>
            {t("Who has access there, can then access and use the original email including its attachments. ")}
          </strong>
          {t("The email will be moved into your postbox ")}
          <strong>{t('"Linked emails".')}</strong>
        </span>
      );
    };

    const emailDraftActionsConfig = useMemo(() => {
      return [
        {
          onClick: handleDelete,
          icon: ["far", "trash"],
          label: t("Delete"),
        },
      ];
    }, [handleDelete]);

    const actionsConfig = useMemo(() => {
      if (emailListStore.emailType === "DRAFT") {
        return {
          primaryActions: emailDraftActionsConfig,
          dropdownActions: [],
        };
      }

      const primaryActions: PrimaryActionsConfig[] = [];
      const dropdownActions: DropdownActionConfig[] = [
        {
          onClick: handleDownloadPdf,
          icon: ["far", "file-pdf"],
          label: t("Download as PDF"),
        },
      ];

      if (emailListStore.allowedToMoveToProject) {
        dropdownActions.push({
          onClick: (emailId) => handleMoveToProject(emailId),
          icon: ["far", "folder-tree"],
          label: t("Link with project..."),
          message: linkedMailToolTipText(),
        });
      }

      if (emailListStore.allowedToUnArchive) {
        dropdownActions.push({
          onClick: handleUnArchive,
          icon: ["far", "file-archive"],
          label: t("Restore"),
        });
      }

      if (emailListStore.allowedToArchive) {
        dropdownActions.push({
          onClick: handleArchive,
          icon: ["far", "file-archive"],
          label: t("Archive"),
        });
      }

      if (emailListStore.allowedToDelete) {
        dropdownActions.push({
          onClick: handleDelete,
          icon: ["far", "trash"],
          label: t("Delete"),
        });
      }

      return { primaryActions, dropdownActions, dropdownFirst: true };
    }, [
      emailListStore.emailType,
      emailListStore.allowedToMoveToProject,
      emailListStore.allowedToUnArchive,
      emailListStore.allowedToArchive,
      emailListStore.allowedToDelete,
      emailDraftActionsConfig,
      handleDownloadPdf,
      handleMoveToProject,
      handleUnArchive,
      handleArchive,
      handleDelete,
      linkedMailToolTipText,
    ]);

    const handleSearch = async (text: string) => {
      try {
        const postObject: API_MailSearchRequest = {
          searchText: text,
          subject: text,
          emailTypes: [emailTypeForSearch],
          orConnection: true,
        };
        await emailSearchStore.submitSearch(postObject);
        // tslint:disable-next-line:max-line-length
        RouterStore.push(
          `/project/${projectsDataStore.projectId}/email/${emailTypeForSearch}/${MAIL_LINK.SEARCH_RESULTS}`
        );
      } catch (err) {
        throw err;
      }
    };

    const handleSelectAll = async () => {
      if (selectedIds.length) {
        setSelectedIds([]);
        setSelectedEmails([]);
      } else {
        const allEmails = await emailListStore.selectAll();
        setSelectedIds(allEmails.map((e) => e.id));
        setSelectedEmails(allEmails);
      }
    };

    const handleNavigateToList = () => {
      navigate(`/project/${projectsDataStore.projectId}/email/${MAIL_LINK[emailListStore.emailType]}`);
    };

    const handleGoBack = () => {
      navigate("..");
    };

    const isTopicInbox = location.pathname.includes("topic-inbox");

    return (
      <>
        <Routes>
          <Route
            path={`${MAIL_LINK.SEARCH_RESULTS}/*`}
            element={<EmailSearchResultsPage accessLevel={props.accessLevel} onSearch={handleSearch} />}
          />
          <Route
            path="*"
            element={
              <div className="flex flex-with-basis" style={{ flexDirection: "column" }}>
                {layoutMode === "LIST" ? (
                  <DefaultLayout
                    isSearchBarVisible={!isTopicInbox}
                    onRefresh={refreshList}
                    onSearch={handleSearch}
                    emailListStore={emailListStore}
                    actionsConfig={actionsConfig}
                    onSelectRow={handleSelectRow}
                    selectedRowKeys={selectedIds}
                    onLayoutChange={handleChangeLayout}
                    layoutMode={layoutMode}
                    onSort={handleSort}
                    isIntermediate={isIntermediate}
                    isSelectedAll={isSelectedAll}
                    onSelectAll={handleSelectAll}
                  />
                ) : (
                  <OutlookLayout
                    onRefresh={refreshList}
                    isSearchBarVisible={!isTopicInbox}
                    onSearch={handleSearch}
                    emailListStore={emailListStore}
                    actionsConfig={actionsConfig}
                    onSelectRow={handleSelectRow}
                    selectedRowKeys={selectedIds}
                    onLayoutChange={handleChangeLayout}
                    layoutMode={layoutMode}
                    onSort={handleSort}
                    isIntermediate={isIntermediate}
                    isSelectedAll={isSelectedAll}
                    onSelectAll={handleSelectAll}
                  />
                )}
              </div>
            }
          />
        </Routes>
      </>
    );
  })
);
